import { type HTMLAttributes } from 'react'
import {
  FormControl,
  InputLabel,
  // reason: this is the wrapped component which we will allow since this is the exported version
  // eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
  Select as MuiSelect,
  type SelectProps as MuiSelectProps,
  type TextFieldProps as MuiTextFieldProps,
} from '@mui/material'
import { FormHelperText } from '../FormHelperText'

import { type DataTestIdAttribute, type MinimalSxProp } from '../../types'

type BaseProps<T> = Pick<
  MuiSelectProps<T>,
  | 'children'
  | 'disabled'
  | 'fullWidth'
  | 'inputRef'
  | 'multiple'
  | 'name'
  | 'native'
  | 'onChange'
  | 'onClose'
  | 'required'
  | 'error'
> & {
  value?: T

  maxHeight?: number

  /**
   * Sometimes your placement of your Menu can be a little off. With this
   * you can correct it by adding a little spacing to the left. Its dirty
   * but we can not fix its internals.
   */
  correction?: number

  readOnly?: boolean
} & Pick<HTMLAttributes<HTMLSelectElement>, 'id'> &
  DataTestIdAttribute &
  MinimalSxProp &
  Pick<MuiTextFieldProps, 'helperText'>

type SelectSmallSizeProps<T> = {
  size: 'small'
  label?: MuiSelectProps<T>['label']
}

type SelectMediumLargeProps<T> = {
  /**
   * @default 'medium'
   */
  size?: 'medium' | 'large'
  label: MuiSelectProps<T>['label']
}

export type SelectProps<T> = BaseProps<T> & (SelectSmallSizeProps<T> | SelectMediumLargeProps<T>)

/**
 * Allways use the type passed to the Select component :)
 * using string as our default when not passed
 */
export type SelectChangeEvent<T = string> = Parameters<NonNullable<SelectProps<T>['onChange']>>[0]

/**
 * Select
 *
 * Demos:
 * - [Select](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/inputs-select--docs)
 *
 */

export const Select = <T = string,>({
  name,
  required,
  label,
  size = 'medium',
  fullWidth,
  maxHeight,
  error,
  helperText,
  correction,
  sx,
  ...props
}: SelectProps<T>) => (
  <FormControl fullWidth={fullWidth} error={error} variant="outlined" size={size} sx={sx}>
    {label && (
      <InputLabel htmlFor={name} required={required} disabled={props.disabled}>
        {label}
      </InputLabel>
    )}
    <MuiSelect<T>
      {...props}
      size={size}
      name={name}
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        slotProps: {
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              ml: correction,
              border: ({ misc }) => misc.borderStyle('grey.300', 1),
            },
            style: {
              ...(maxHeight ? { maxHeight } : {}),
            },
          },
        },
      }}
    />

    {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
  </FormControl>
)
